import { useEffect, useState, useContext } from "react";
import * as Yup from "yup";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { FiInfo } from "react-icons/fi";
import { MdEdit, MdAdd } from "react-icons/md";
import { Trash2 } from "lucide-react";
import { IoMdArrowRoundBack } from "react-icons/io";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  AccessDenied,
  Spinner,
  GenericTable,
  Pagination,
} from "../../Components/Common";
import { UserContext } from "../../Context/UserContext";
import {
  getAllAnnouncements,
  editAnnouncementById,
  deleteAnnouncementById,
  createAnnouncement,
} from "../../Services/announcementService";
import {
  STATUS_TYPES,
  ANNOUNCEMENT_COLUMNS,
  ANNOUNCEMENT_LABELS,
  TOAST_MESSAGES,
  COLUMN_WIDTHS,
} from "./constants";
import { extractErrorMessage } from "../../Utils/extractErrorMessage.js";

const AnnouncementList = () => {
  const navigate = useNavigate();
  const { isHeadOfficeAdmin } = useContext(UserContext);

  const [announcements, setAnnouncements] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(STATUS_TYPES.ACTIVE);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [selectedViewAnnouncement, setSelectedViewAnnouncement] =
    useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: "",
    content: "",
    redirect_url: "",
    start_date: "",
    end_date: "",
    is_active: true,
  });

  const validationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    content: Yup.string().required("Content is required"),
    redirect_url: Yup.string()
      .url("Redirect URL must be a valid URL")
      .nullable(),
    start_date: Yup.date().required("Start date is required"),
    end_date: Yup.date().min(
      Yup.ref("start_date"),
      "End date cannot be before start date"
    ),
    is_active: Yup.string().required("Status is required"),
  });

  const fetchAnnouncements = async () => {
    try {
      setTableLoading(true);

      const params = {
        limit: rowsPerPage,
        offset: (page - 1) * rowsPerPage,
        is_active: activeTab === STATUS_TYPES.ACTIVE,
      };

      const res = await getAllAnnouncements(params);

      if (res.status === 200) {
        setAnnouncements(res.data.results);
        setTotalPages(Math.max(1, Math.ceil(res.data.count / rowsPerPage)));
      } else {
        setAnnouncements([]);
        toast.error(TOAST_MESSAGES.FETCH_ANNOUNCEMENT_FAILED);
      }
    } catch (error) {
      const msg =
        extractErrorMessage(error) || TOAST_MESSAGES.FETCH_ANNOUNCEMENT_ERROR;
      toast.error(msg);
    } finally {
      setTableLoading(false);
    }
  };

  const handleViewAnnouncement = (announcement) => {
    setSelectedViewAnnouncement(announcement);
    setIsViewModalOpen(true);
  };

  const handleEditAnnouncement = (announcement) => {
    setSelectedAnnouncement(announcement);
    setIsEditModalOpen(true);
  };

  const handleUpdateAnnouncement = async () => {
    if (!selectedAnnouncement) return;

    try {
      setTableLoading(true);
      const res = await editAnnouncementById(
        selectedAnnouncement,
        selectedAnnouncement.id
      );
      if (res.status === 200 || res.status === 204) {
        toast.success(TOAST_MESSAGES.UPDATE_ANNOUNCEMENT_SUCCESS);
        fetchAnnouncements(page);
        setIsEditModalOpen(false);
        setSelectedAnnouncement(null);
      } else {
        toast.error(TOAST_MESSAGES.UPDATE_ANNOUNCEMENT_FAILED);
      }
    } catch (err) {
      const msg =
        extractErrorMessage(err) || TOAST_MESSAGES.UPDATE_ANNOUNCEMENT_ERROR;
      toast.error(msg);
    } finally {
      setTableLoading(false);
    }
  };

  const handleDeleteAnnouncement = async (announcement) => {
    if (!window.confirm("Are you sure you want to delete this announcement?"))
      return;

    try {
      setTableLoading(true);
      const res = await deleteAnnouncementById(announcement.id);
      if (res.status === 204) {
        toast.success(TOAST_MESSAGES.DELETE_ANNOUNCEMENT_SUCCESS);
        fetchAnnouncements(page);
      } else {
        toast.error(TOAST_MESSAGES.DELETE_ANNOUNCEMENT_FAILED);
      }
    } catch (err) {
      toast.error(TOAST_MESSAGES.DELETE_ANNOUNCEMENT_ERROR);
    } finally {
      setTableLoading(false);
    }
  };

  const handleAddAnnouncement = () => {
    setNewAnnouncement({
      title: "",
      content: "",
      redirect_url: "",
      start_date: "",
      end_date: "",
      is_active: true,
    });
    setIsAddModalOpen(true);
  };

  const handleSubmitAnnouncement = async (values, { setSubmitting }) => {
    setSubmitting(true);
    try {
      setTableLoading(true);

      const res = await createAnnouncement(values);

      if (res.status === 201 || res.status === 200) {
        toast.success(TOAST_MESSAGES.ADD_ANNOUNCEMENT_SUCCESS);
        setIsAddModalOpen(false);
        fetchAnnouncements(page);
      } else {
        toast.error(TOAST_MESSAGES.ADD_ANNOUNCEMENT_FAILED);
      }
    } catch (err) {
      toast.error(
        extractErrorMessage(err) || TOAST_MESSAGES.ADD_ANNOUNCEMENT_ERROR
      );
    } finally {
      setTableLoading(false);
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (isHeadOfficeAdmin) {
      fetchAnnouncements(page);
    }
  }, [isHeadOfficeAdmin, page, rowsPerPage, activeTab]);

  if (!isHeadOfficeAdmin) return <AccessDenied />;

  const renderMobileFields = (row) => {
    return (
      <div
        className="border-b-2 rounded-md p-3 mb-3 bg-white"
        onClick={() => handleViewAnnouncement(row)}
      >
        <div className="font-semibold text-gray-900 text-base mb-2">
          {row.title}
        </div>

        <div className="grid grid-cols-4 items-center mb-2">
          <div className="col-span-2 text-gray-700 text-sm">
            Start date: {new Date(row.start_date).toLocaleDateString()}
          </div>

          <div
            className={`col-span-1 text-sm font-semibold flex justify-center items-center ${
              row.is_active ? "text-green-600" : "text-red-600"
            }`}
          >
            {row.is_active ? "Active" : "Inactive"}
          </div>

          <div
            className="col-span-1 flex justify-center items-center gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <MdEdit
              size={18}
              className="text-primary cursor-pointer"
              onClick={() => handleEditAnnouncement(row)}
            />
            <Trash2
              size={18}
              className="text-red-600 cursor-pointer"
              onClick={() => handleDeleteAnnouncement(row)}
            />
          </div>
        </div>

        <div className="text-gray-700 text-sm">
          End date:{" "}
          {row.end_date ? new Date(row.end_date).toLocaleDateString() : "-"}
        </div>
      </div>
    );
  };

  const renderField = (key, row) => {
    const content = (() => {
      switch (key) {
        case "title":
          return <span className="font-medium text-gray-900">{row.title}</span>;
        case "redirect_url":
          return row.redirect_url ? (
            <a
              href={row.redirect_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary pl-6"
            >
              Link
            </a>
          ) : (
            "-"
          );
        case "start_date":
          return new Date(row.start_date).toLocaleDateString();
        case "end_date":
          return row.end_date
            ? new Date(row.end_date).toLocaleDateString()
            : "-";
        case "status":
          return (
            <span
              className={`font-semibold ${
                row.is_active ? "text-green-600" : "text-red-600"
              }`}
            >
              {row.is_active ? STATUS_TYPES.ACTIVE : STATUS_TYPES.INACTIVE}
            </span>
          );
        case "actions":
          return (
            <div className="flex gap-6">
              <FiInfo
                size={20}
                onClick={() => handleViewAnnouncement(row)}
                className="text-blue-600 cursor-pointer"
              />
              <MdEdit
                size={20}
                onClick={() => handleEditAnnouncement(row)}
                className="text-primary cursor-pointer"
              />

              <Trash2
                size={20}
                onClick={() => handleDeleteAnnouncement(row)}
                className="text-red-600 cursor-pointer"
              />
            </div>
          );
        default:
          return row[key] ?? "-";
      }
    })();

    return <div className={`${COLUMN_WIDTHS[key] || ""}`}>{content}</div>;
  };

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between my-4 md:gap-4 gap-2">
        <div className="flex justify-between md:gap-3 gap-1 my-2 md:my-0 items-end md:items-center">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-primary text-center text-sm font-lg md:pl-2 pl-1"
          >
            <IoMdArrowRoundBack size={22} className="text-primary" />
          </button>
          <h1 className="text-black text-xl md:text-2xl font-semibold pt-2 md:py-3 whitespace-nowrap">
            Manage Announcements
          </h1>
        </div>

        <button
          onClick={handleAddAnnouncement}
          className={`
            px-1 py-1 rounded-md flex items-center gap-1 bg-primary text-white hover:bg-blue-700 mx-2
            md:px-3 md:py-2 md:gap-2
          `}
          aria-label="Add Announcement"
        >
          <MdAdd size={18} />
          <span className="md:whitespace-nowrap text-sm md:text-base">
            Add Announcement
          </span>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex justify-start items-center pb-1 px-2 md:px-4">
        {[STATUS_TYPES.ACTIVE, STATUS_TYPES.INACTIVE].map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              setPage(1);
            }}
            className={`px-4 py-1 font-bold border-b-2 text-sm md:text-base ${
              activeTab === tab
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-600 hover:text-gray-800"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-x-auto sm:rounded-lg mt-2">
        {tableLoading ? (
          <div className="flex items-center justify-center h-[60vh]">
            <Spinner />
          </div>
        ) : (
          <div className="w-full flex flex-col h-[61vh] md:h-[54vh] overflow-x-auto px-1">
            <GenericTable
              tableColumns={ANNOUNCEMENT_COLUMNS}
              tableRows={announcements}
              columnLabels={ANNOUNCEMENT_LABELS}
              renderField={renderField}
              enableGridLayout={true}
              mobileGridColumns={3}
              renderMobileCard={renderMobileFields}
            />
          </div>
        )}
      </div>

      {announcements.length > 0 && (
        <div className="flex justify-end pb-4 px-4 mt-2">
          <Pagination
            page={page}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onPageSizeChange={setRowsPerPage}
          />
        </div>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && selectedAnnouncement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl p-2 w-full max-w-3xl relative">
            <div className="px-6 py-2 border-b">
              <h2 className="text-2xl font-bold">Edit Announcement</h2>
            </div>

            <div className="px-6 py-3 space-y-3">
              <div className="space-y-2">
                <div>
                  <label className="block mb-1 font-medium text-gray-700">
                    Title
                  </label>
                  <input
                    type="text"
                    value={selectedAnnouncement.title}
                    onChange={(e) =>
                      setSelectedAnnouncement({
                        ...selectedAnnouncement,
                        title: e.target.value,
                      })
                    }
                    className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="Enter title"
                  />
                </div>

                <div>
                  <label className="block mb-1 font-medium text-gray-700">
                    Content
                  </label>
                  <textarea
                    value={selectedAnnouncement.content || ""}
                    onChange={(e) =>
                      setSelectedAnnouncement({
                        ...selectedAnnouncement,
                        content: e.target.value,
                      })
                    }
                    className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                    rows={3}
                    placeholder="Enter content"
                  />
                </div>

                <div>
                  <label className="block mb-1 font-medium text-gray-700">
                    Redirect URL
                  </label>
                  <input
                    type="text"
                    value={selectedAnnouncement.redirect_url || ""}
                    onChange={(e) =>
                      setSelectedAnnouncement({
                        ...selectedAnnouncement,
                        redirect_url: e.target.value,
                      })
                    }
                    className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="Enter redirect URL (optional)"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-1 font-medium text-gray-700">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={
                        selectedAnnouncement.start_date?.split("T")[0] || ""
                      }
                      onChange={(e) =>
                        setSelectedAnnouncement({
                          ...selectedAnnouncement,
                          start_date: e.target.value,
                        })
                      }
                      className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 font-medium text-gray-700">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={selectedAnnouncement.end_date?.split("T")[0] || ""}
                      onChange={(e) =>
                        setSelectedAnnouncement({
                          ...selectedAnnouncement,
                          end_date: e.target.value,
                        })
                      }
                      className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>
                </div>

                <div>
                  <label className="block mb-1 font-medium text-gray-700">
                    Status
                  </label>
                  <select
                    value={
                      selectedAnnouncement.is_active ? "active" : "inactive"
                    }
                    onChange={(e) =>
                      setSelectedAnnouncement({
                        ...selectedAnnouncement,
                        is_active: e.target.value === "active",
                      })
                    }
                    className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="px-6 py-3 flex justify-end gap-3">
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="bg-red-600 hover:bg-red-800 text-white px-5 py-2 rounded-lg font-medium transition"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateAnnouncement}
                className="bg-primary hover:bg-blue-900 text-white px-5 py-2 rounded-lg font-medium transition"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {isViewModalOpen && selectedViewAnnouncement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-2">
          <div className="bg-white rounded-xl shadow-xl p-3 w-full max-w-3xl relative">
            <div className="px-6 py-2 border-b">
              <h2 className="text-2xl font-bold">
                {selectedViewAnnouncement.title}
              </h2>
              <p className="text-gray-500 text-sm mt-1">Announcement Details</p>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-1">Content</h3>
                <p className="text-gray-700">
                  {selectedViewAnnouncement.content || "No content available."}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-md font-semibold ">Redirect URL</h4>
                  <p className="text-gray-800">
                    {selectedViewAnnouncement.redirect_url || "-"}
                  </p>
                </div>
                <div>
                  <h4 className="text-md font-semibold ">Status</h4>
                  <p className="text-gray-800">
                    {selectedViewAnnouncement.is_active ? "Active" : "Inactive"}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-md font-semibold ">Start Date</h4>
                  <p className="text-gray-800">
                    {new Date(
                      selectedViewAnnouncement.start_date
                    ).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <h4 className="text-md font-semibold ">End Date</h4>
                  <p className="text-gray-800">
                    {selectedViewAnnouncement.end_date
                      ? new Date(
                          selectedViewAnnouncement.end_date
                        ).toLocaleDateString()
                      : "-"}
                  </p>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t flex justify-end">
              <button
                onClick={() => setIsViewModalOpen(false)}
                className="bg-primary hover:bg-blue-800 text-white px-5 py-2 rounded-lg font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-3">
          <div className="bg-white rounded-xl shadow-xl p-2 w-full max-w-3xl relative">
            <div className="px-6 py-2 border-b">
              <h2 className="text-2xl font-bold">Add Announcement</h2>
            </div>

            <Formik
              initialValues={newAnnouncement}
              validationSchema={validationSchema}
              onSubmit={handleSubmitAnnouncement}
            >
              {({ isSubmitting }) => (
                <Form className="px-6 py-2 space-y-2">
                  {/* Title */}
                  <div>
                    <label className="block mb-1 font-medium text-gray-700">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <Field
                      name="title"
                      type="text"
                      className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                      placeholder="Enter title"
                    />
                    <ErrorMessage
                      name="title"
                      component="div"
                      className="text-red-500 text-xs mt-[2px]"
                    />
                  </div>

                  {/* Content */}
                  <div>
                    <label className="block mb-1 font-medium text-gray-700">
                      Content <span className="text-red-500">*</span>
                    </label>
                    <Field
                      as="textarea"
                      name="content"
                      rows={3}
                      className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                      placeholder="Enter content"
                    />
                    <ErrorMessage
                      name="content"
                      component="div"
                      className="text-red-500 text-xs mt-[2px]"
                    />
                  </div>

                  {/* Redirect URL */}
                  <div>
                    <label className="block mb-1 font-medium text-gray-700">
                      Redirect URL
                    </label>
                    <Field
                      name="redirect_url"
                      type="url"
                      className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                      placeholder="Enter redirect URL (optional)"
                    />
                    <ErrorMessage
                      name="redirect_url"
                      component="div"
                      className="text-red-500 text-xs mt-[2px]"
                    />
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block mb-1 font-medium text-gray-700">
                        Start Date <span className="text-red-500">*</span>
                      </label>
                      <Field
                        name="start_date"
                        type="date"
                        className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                      />
                      <ErrorMessage
                        name="start_date"
                        component="div"
                        className="text-red-500 text-xs mt-[2px]"
                      />
                    </div>

                    <div>
                      <label className="block mb-1 font-medium text-gray-700">
                        End Date <span className="text-red-500">*</span>
                      </label>
                      <Field
                        name="end_date"
                        type="date"
                        className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                      />
                      <ErrorMessage
                        name="end_date"
                        component="div"
                        className="text-red-500 text-xs mt-[2px]"
                      />
                    </div>
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block mb-1 font-medium text-gray-700">
                      Status <span className="text-red-500">*</span>
                    </label>
                    <Field
                      as="select"
                      name="is_active"
                      className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </Field>
                    <ErrorMessage
                      name="is_active"
                      component="div"
                      className="text-red-500 text-xs mt-[2px]"
                    />
                  </div>

                  {/* Buttons */}
                  <div className="flex justify-end gap-3 pt-3 mt-3">
                    <button
                      type="button"
                      onClick={() => setIsAddModalOpen(false)}
                      className="bg-red-600 hover:bg-red-800 text-white px-5 py-2 rounded-lg font-medium transition"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-primary hover:bg-blue-900 text-white px-5 py-2 rounded-lg font-medium transition"
                    >
                      {isSubmitting ? <Spinner /> : "Save"}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnouncementList;
